import {
  getWorkflowType,
  getNodes,
  getWorkflowHasUnsavedChanges,
  getWorkflow,
} from 'modules/workflows/ducks';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { isSameMinute } from 'date-fns';

type UseJourneyReviewRefresherProps = {
  validateJourney: () => void;
  getOptimizations: () => void;
  clearCompleted: () => void;
};

export const useJourneyReviewRefresher = ({
  validateJourney,
  getOptimizations,
  clearCompleted,
}: UseJourneyReviewRefresherProps) => {
  const [firstTimePanelOpening, setFirstTimePanelOpening] = useState(true);
  const [shouldRefreshJourneyReview, setRefreshJourneyReview] = useState(false);
  const hasUnsavedChanges = useSelector(getWorkflowHasUnsavedChanges);
  const workflowType = useSelector(getWorkflowType);
  const workflow = useSelector(getWorkflow);

  const createdAt = workflow?.createdAt;
  const isNewJourney = useMemo(
    () => isSameMinute(new Date(), new Date(createdAt || '')),
    [createdAt],
  );

  const nodes = useSelector(getNodes);

  const isReviewableJourneyLoaded = useMemo(() => {
    if (!isNewJourney && (workflowType.draftOnly || workflowType.isDraft)) {
      return nodes.length >= 1;
    }
    return false;
  }, [nodes, workflowType, isNewJourney]);

  /*
    When we first load the app and the nodes are fetched from the server, we need to run the checks to see if there are any issues.  We are
    not showing the review panel until the user opens it. If for some reason the original checks didn't work or failed, we can run them for the
    first time when they hit the review button.
  */

  useEffect(() => {
    if (firstTimePanelOpening && isReviewableJourneyLoaded) {
      setRefreshJourneyReview(true);
    }
  }, [firstTimePanelOpening, isReviewableJourneyLoaded]);

  useEffect(() => {
    if (!hasUnsavedChanges && shouldRefreshJourneyReview) {
      clearCompleted();
      validateJourney();
      getOptimizations();
      setFirstTimePanelOpening(false);
      setRefreshJourneyReview(false);
    }
  }, [hasUnsavedChanges, shouldRefreshJourneyReview, isReviewableJourneyLoaded]);

  useEffect(() => {
    if (!firstTimePanelOpening && workflowType.isPublished) {
      // reset so that panel will fetch when we switch workflows again.
      setFirstTimePanelOpening(true);
    }
  }, [workflowType]);

  const handleRefresh = () => {
    setFirstTimePanelOpening(false);
    setRefreshJourneyReview(true);
  };

  return {
    shouldRefreshJourneyReview,
    refreshJourneyReview: handleRefresh,
    firstTimePanelOpening,
    setFirstTimePanelOpening,
  };
};
